/**
 * My Companies Page
 * Shows companies owned by the authenticated user (approved claims only)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  PencilSquareIcon,
  EyeIcon,
  ArrowPathIcon,
  XCircleIcon,
  PlusCircleIcon,
  CheckBadgeIcon,
  FunnelIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/common/SEO';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';
import EditCompanyModal from '../components/company/EditCompanyModal';

interface Company {
  id: string;
  slug: string;
  nameTh: string;
  nameEn: string;
  province: string;
  companySize: string;
  isVerified: boolean;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Ownership {
  id: string;
  userId: string;
  companyId: string;
  isPrimary: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canTransfer: boolean;
  isActive: boolean;
  isApproved: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  company: Company;
}

interface OwnershipListResponse {
  ownerships: Ownership[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

const MyCompaniesPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    per_page: 20,
    has_next: false,
    has_prev: false
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [loadingCompanyDetails, setLoadingCompanyDetails] = useState(false);

  // Filter and Sort states
  const [filterProvince, setFilterProvince] = useState<string>('all');
  const [filterSize, setFilterSize] = useState<string>('all');
  const [filterVerified, setFilterVerified] = useState<boolean | null>(null);
  const [filterFeatured, setFilterFeatured] = useState<boolean | null>(null);
  const [sortBy, setSortBy] = useState<string>('updatedAt-desc');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch companies data
  useEffect(() => {
    if (isAuthenticated) {
      fetchCompanies();
    }
  }, [isAuthenticated, pagination.page]);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get<OwnershipListResponse>(
        `/api/ownership/claims?page=${pagination.page}&per_page=${pagination.per_page}`
      );

      // Filter for approved claims only and extract company data
      const approvedCompanies = response.data.ownerships
        .filter(ownership => ownership.isApproved && ownership.isActive)
        .map(ownership => ownership.company);

      setCompanies(approvedCompanies);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        per_page: response.data.per_page,
        has_next: response.data.has_next,
        has_prev: response.data.has_prev
      });
    } catch (err: any) {
      console.error('Failed to fetch companies:', err);
      setError(err.response?.data?.detail || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch full company details for editing
  const fetchCompanyDetails = async (slug: string) => {
    try {
      setLoadingCompanyDetails(true);
      const response = await apiClient.get(`/api/companies/${slug}`);
      return response.data;
    } catch (err: any) {
      console.error('Failed to fetch company details:', err);
      toast.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
      return null;
    } finally {
      setLoadingCompanyDetails(false);
    }
  };

  // Handle edit company button click
  const handleEditCompany = async (company: Company) => {
    // Fetch full company details before opening modal (using slug, not id)
    const fullCompanyData = await fetchCompanyDetails(company.slug);
    if (fullCompanyData) {
      setSelectedCompany(fullCompanyData);
      setEditModalOpen(true);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get company size label
  const getCompanySizeLabel = (size: string) => {
    const sizeMap: { [key: string]: string } = {
      'SMALL': 'ขนาดเล็ก',
      'MEDIUM': 'ขนาดกลาง',
      'LARGE': 'ขนาดใหญ่',
      'ENTERPRISE': 'องค์กรขนาดใหญ่'
    };
    return sizeMap[size] || size;
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get unique provinces from companies
  const uniqueProvinces = useMemo(() => {
    const provinces = companies.map(c => c.province);
    return Array.from(new Set(provinces)).sort();
  }, [companies]);

  // Filter and sort companies
  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = [...companies];

    // Apply filters
    if (filterProvince !== 'all') {
      filtered = filtered.filter(c => c.province === filterProvince);
    }

    if (filterSize !== 'all') {
      filtered = filtered.filter(c => c.companySize === filterSize);
    }

    if (filterVerified !== null) {
      filtered = filtered.filter(c => c.isVerified === filterVerified);
    }

    if (filterFeatured !== null) {
      filtered = filtered.filter(c => c.isFeatured === filterFeatured);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'updatedAt-desc':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'updatedAt-asc':
          return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        case 'name-asc':
          return a.nameTh.localeCompare(b.nameTh, 'th');
        case 'name-desc':
          return b.nameTh.localeCompare(a.nameTh, 'th');
        case 'province-asc':
          return a.province.localeCompare(b.province, 'th');
        default:
          return 0;
      }
    });

    return filtered;
  }, [companies, filterProvince, filterSize, filterVerified, filterFeatured, sortBy]);

  // Reset filters
  const handleResetFilters = () => {
    setFilterProvince('all');
    setFilterSize('all');
    setFilterVerified(null);
    setFilterFeatured(null);
    setSortBy('updatedAt-desc');
  };

  // Check if any filter is active
  const hasActiveFilters = filterProvince !== 'all' || filterSize !== 'all' ||
                           filterVerified !== null || filterFeatured !== null ||
                           sortBy !== 'updatedAt-desc';

  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <>
      <SEO
        title="บริษัทของฉัน | Thai Security Directory"
        description="จัดการข้อมูลบริษัทของคุณ"
        url="/my-companies"
      />

      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-8 sm:py-12">
        <div className="container-wrapper max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Breadcrumbs */}
            <Breadcrumbs />

            {/* Page Header */}
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-2">
                  บริษัทของฉัน
                </h1>
                <p className="text-neutral-600">
                  จัดการข้อมูลบริษัทที่คุณเป็นเจ้าของ
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/my-requests')}
                  className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-all duration-200"
                >
                  <span>คำขอของฉัน</span>
                </button>
                <button
                  onClick={() => navigate('/create-company')}
                  className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <PlusCircleIcon className="w-5 h-5" />
                  <span>เพิ่มบริษัทใหม่</span>
                </button>
              </div>
            </div>

            {/* Filter and Sort Controls */}
            {!isLoading && companies.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 mb-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <FunnelIcon className="w-5 h-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-neutral-900">กรองและเรียงลำดับ</h3>
                  </div>
                  {hasActiveFilters && (
                    <button
                      onClick={handleResetFilters}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      รีเซ็ตทั้งหมด
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Province Filter */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      จังหวัด
                    </label>
                    <select
                      value={filterProvince}
                      onChange={(e) => setFilterProvince(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="all">ทั้งหมด</option>
                      {uniqueProvinces.map(province => (
                        <option key={province} value={province}>{province}</option>
                      ))}
                    </select>
                  </div>

                  {/* Company Size Filter */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      ขนาดบริษัท
                    </label>
                    <select
                      value={filterSize}
                      onChange={(e) => setFilterSize(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="all">ทั้งหมด</option>
                      <option value="SMALL">ขนาดเล็ก</option>
                      <option value="MEDIUM">ขนาดกลาง</option>
                      <option value="LARGE">ขนาดใหญ่</option>
                      <option value="ENTERPRISE">องค์กรขนาดใหญ่</option>
                    </select>
                  </div>

                  {/* Verified Filter */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      ตรวจสอบแล้ว
                    </label>
                    <select
                      value={filterVerified === null ? 'all' : filterVerified.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilterVerified(value === 'all' ? null : value === 'true');
                      }}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="all">ทั้งหมด</option>
                      <option value="true">ตรวจสอบแล้ว</option>
                      <option value="false">ยังไม่ตรวจสอบ</option>
                    </select>
                  </div>

                  {/* Featured Filter */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      บริษัทแนะนำ
                    </label>
                    <select
                      value={filterFeatured === null ? 'all' : filterFeatured.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilterFeatured(value === 'all' ? null : value === 'true');
                      }}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="all">ทั้งหมด</option>
                      <option value="true">บริษัทแนะนำ</option>
                      <option value="false">ทั่วไป</option>
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      <ArrowsUpDownIcon className="w-4 h-4 inline-block mr-1" />
                      เรียงตาม
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="updatedAt-desc">อัปเดตล่าสุด</option>
                      <option value="updatedAt-asc">อัปเดตเก่าสุด</option>
                      <option value="name-asc">ชื่อบริษัท ก-ฮ</option>
                      <option value="name-desc">ชื่อบริษัท ฮ-ก</option>
                      <option value="province-asc">จังหวัด ก-ฮ</option>
                    </select>
                  </div>
                </div>

                {/* Results Summary */}
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <p className="text-sm text-neutral-600">
                    แสดง <span className="font-semibold text-neutral-900">{filteredAndSortedCompanies.length}</span> จาก <span className="font-semibold text-neutral-900">{companies.length}</span> บริษัท
                    {hasActiveFilters && <span className="text-primary-600 ml-1">(กรองแล้ว)</span>}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Companies List */}
            <div className="space-y-4">
              {isLoading ? (
                // Loading State
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-3 text-primary-600">
                    <ArrowPathIcon className="w-6 h-6 animate-spin" />
                    <span className="text-lg font-medium">กำลังโหลดข้อมูล...</span>
                  </div>
                </div>
              ) : error ? (
                // Error State
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                  <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-red-900 mb-2">เกิดข้อผิดพลาด</h3>
                  <p className="text-red-700 mb-4">{error}</p>
                  <button
                    onClick={fetchCompanies}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200"
                  >
                    ลองใหม่อีกครั้ง
                  </button>
                </div>
              ) : companies.length === 0 ? (
                // Empty State
                <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
                  <BuildingOfficeIcon className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    ยังไม่มีบริษัท
                  </h3>
                  <p className="text-neutral-600 mb-6">
                    คุณยังไม่มีบริษัทที่เป็นเจ้าของ ค้นหาบริษัทและส่งคำขอเป็นเจ้าของได้เลย
                  </p>
                  <button
                    onClick={() => navigate('/search')}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    ค้นหาบริษัท
                  </button>
                </div>
              ) : filteredAndSortedCompanies.length === 0 ? (
                // No Results After Filter
                <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
                  <FunnelIcon className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    ไม่พบบริษัทที่ตรงกับเงื่อนไข
                  </h3>
                  <p className="text-neutral-600 mb-6">
                    ลองปรับเงื่อนไขการกรองใหม่อีกครั้ง
                  </p>
                  <button
                    onClick={handleResetFilters}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    รีเซ็ตตัวกรอง
                  </button>
                </div>
              ) : (
                // Companies Grid
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredAndSortedCompanies.map((company) => (
                      <motion.div
                        key={company.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-xl shadow-sm border border-neutral-200 hover:shadow-md transition-all duration-200 overflow-hidden"
                      >
                        <div className="p-6">
                          {/* Company Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-start space-x-3">
                                <BuildingOfficeIcon className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                                    {company.nameTh}
                                  </h3>
                                  {company.nameEn && (
                                    <p className="text-sm text-neutral-600 mb-2">
                                      {company.nameEn}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            {company.isVerified && (
                              <CheckBadgeIcon className="w-6 h-6 text-green-500 flex-shrink-0" title="ตรวจสอบแล้ว" />
                            )}
                          </div>

                          {/* Company Details */}
                          <div className="space-y-2 text-sm text-neutral-600 mb-4">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">จังหวัด:</span>
                              <span>{company.province}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">ขนาด:</span>
                              <span>{getCompanySizeLabel(company.companySize)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">อัปเดตล่าสุด:</span>
                              <span>{formatDate(company.updatedAt)}</span>
                            </div>
                          </div>

                          {/* Status Badges */}
                          <div className="flex flex-wrap items-center gap-2 mb-4">
                            {company.isVerified && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                ✓ ตรวจสอบแล้ว
                              </span>
                            )}
                            {company.isFeatured && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                ⭐ แนะนำ
                              </span>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-3 pt-4 border-t border-neutral-100">
                            <button
                              onClick={() => navigate(`/companies/${company.slug}`)}
                              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors duration-200"
                            >
                              <EyeIcon className="w-4 h-4" />
                              <span>ดูหน้าบริษัท</span>
                            </button>
                            <button
                              onClick={() => handleEditCompany(company)}
                              disabled={loadingCompanyDetails}
                              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loadingCompanyDetails ? (
                                <>
                                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                  <span>กำลังโหลด...</span>
                                </>
                              ) : (
                                <>
                                  <PencilSquareIcon className="w-4 h-4" />
                                  <span>แก้ไขข้อมูล</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination.total > pagination.per_page && (
                    <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-neutral-200 px-6 py-4 mt-6">
                      <div className="text-sm text-neutral-600">
                        แสดง {((pagination.page - 1) * pagination.per_page) + 1} - {Math.min(pagination.page * pagination.per_page, pagination.total)} จาก {pagination.total} บริษัท
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={!pagination.has_prev}
                          className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ก่อนหน้า
                        </button>
                        <span className="px-4 py-2 text-sm font-medium text-neutral-900">
                          หน้า {pagination.page}
                        </span>
                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={!pagination.has_next}
                          className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ถัดไป
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Edit Company Modal */}
      {selectedCompany && (
        <EditCompanyModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedCompany(null);
          }}
          company={selectedCompany}
          onSuccess={() => {
            fetchCompanies();
            toast.success('ส่งคำขอแก้ไขสำเร็จ');
          }}
        />
      )}
    </>
  );
};

export default MyCompaniesPage;
